import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { PetsState } from '@spike/pets-action';
import Staff from 'model/Staff';
import PetCard from 'components/UI/PetCard';
import moment, { Moment } from 'moment-timezone';
import ServiceOptions from './ServiceOptions';
import AppointmentOptions from './AppointmentOptions';
import { DataVaraible, AppointmentMultiple, PetServiceWrapper, itemTypes, ItemData } from 'model/NewMultipleBooking';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';
import { AlertNewBooking } from '../UI/AlertNewBooking';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import Pet from '@spike/pet-model';
import { useTimeZone } from 'hooks';
import { PetVaccine } from '@spike/medical-history-model';
import Service from 'model/Service';

interface Props {
    date: Moment;
    appointment: AppointmentMultiple;
    onAddService: () => void;
    onAddAddOn: (petId: number) => void;
    onAddProduct: (petId: number) => void;
    onAddCustomItem: (petId: number) => void;
    onDelete: (petId: number) => void;
    onEdit: (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes
    ) => void;
    onDeleteAppointment: (petId: number, value?: number | string, type?: itemTypes, dateToRemove?: Moment) => void;
    onShowActions: (
        petId: number,
        dateEdit: Moment,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        staffId?: number,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes
    ) => void;
    onShowPetCard?: (petId: number) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            border: '1px solid #D4D4D4',
            borderRadius: 6,
            paddingLeft: 15,
            paddingTop: 15,
            paddingBottom: 15,
            marginBottom: 20,
            [theme.breakpoints.only('sm')]: {
                cursor: 'pointer'
            }
        },
        divide: {
            borderBottom: '1px solid #D4D4D4'
        },
        serviceStaffContainer: {
            marginRight: 15,
            marginLeft: -15
        },
        serviceName: {
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '14px',
            paddingBottom: 7,
            width: '92%',
            display: 'flex',
            paddingTop: 10
        },
        serviceNameText: {
            maxWidth: '63%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        serviceDataContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: 10
        },
        dataContainer: {
            display: 'flex',
            justifyContent: 'space-between'
        },
        priceAndDuration: {
            fontWeight: 400,
            fontSize: 14,
            lineHeight: '14px',
            color: '#7A7A7A'
        },
        spacingDuration: {
            paddingRight: 3
        },
        addService: {
            cursor: 'pointer',
            color: '#92B4A7',
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '27px',
            paddingTop: 10
        },
        addServiceBorder: {
            borderTop: '1px solid #D4D4D4'
        },
        spaceClock: {
            paddingRight: '3px !important'
        },
        spacing: {
            paddingBottom: 10
        },
        leftSpace: {
            paddingLeft: '5px'
        },
        petBox: {
            display: 'flex',
            justifyContent: 'space-between',
            marginRight: 15
        },
        petCardContainer: {
            width: '85%'
        },
        deleteBox: {
            display: 'flex',
            width: '15%',
            justifyContent: 'flex-end'
        },
        deleteContainer: {
            width: 38,
            height: 38,
            cursor: 'pointer',
            background: '#F1F1F1',
            borderRadius: '50%',
            alignContent: 'center',
            textAlign: 'center'
        },
        dateContainer: {
            height: 30,
            backgroundColor: '#F8F5F1',
            borderRadius: 4,
            alignContent: 'center',
            marginRight: 15
        },
        date: {
            paddingLeft: 15,
            fontSize: 16,
            lineHeight: '16px',
            fontWeight: 600
        },
        withoutDivider: {
            borderBottom: 'none !important'
        },
        serviceData: {
            display: 'flex'
        },
        price: {
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '14px',
            paddingBottom: 7,
            paddingLeft: 4
        },
        beforePrice: {
            textDecoration: 'line-through',
            color: '#7A7A7A',
            marginRight: 2
        },
        rightSpace: {
            paddingRight: 5
        },
        alert: {
            marginBottom: 20,
            minHeight: 39,
            padding: '5px 14px',
            borderRadius: 10,
            alignContent: 'center'
        },
        alertText: {
            fontWeight: 400,
            fontSize: 14,
            lineHeight: '120%',
            paddingTop: 2
        },
        update: {
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline'
        },
        bold: {
            fontWeight: 600
        },
        serviceOptions: {
            alignSelf: 'center'
        },
        alertAndAddContainer: {
            marginRight: 15
        },
        itemContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
        },
        emptyBox: {
            paddingRight: 9,
            borderLeft: '6px solid #ffffff',
            borderBottom: 'none'
        },
        serviceOptionsContainer: {
            height: 'initial',
            display: 'flex',
            alignItems: 'center'
        }
    })
);

export const AppointmentCard: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const timezone = useTimeZone();

    const petsState = useSelector<RootState, PetsState>(state => state.pets);

    const services = useSelector<RootState, Array<Service>>(state => state.services.services);

    const staffsState = useSelector<RootState, Array<Staff>>(state => state.staff.staff);

    const [appointmentsSeparated, setAppointmentsSeparated] = useState<Array<Array<DataVaraible>> | undefined>(
        undefined
    );

    const [pet, setPet] = useState<Pet | undefined>(undefined);

    const formatStaffToStaffMember = (staffId: number) => {
        const staffSelected = staffsState.find(staff => staff.id === staffId);
        return staffSelected?.person.firstName;
    };

    const getServicesTime = (serv?: PetServiceWrapper) => {
        const timeInMinutes = serv?.durationInMinutes;
        return timeInMinutes
            ? timeInMinutes / 60 > 1
                ? `${Math.trunc(timeInMinutes! / 60)}hrs ${timeInMinutes! % 60 !== 0 ? timeInMinutes! % 60 : ''}${
                      timeInMinutes! % 60 !== 0 ? 'min' : ''
                  }`
                : `${timeInMinutes! === 60 ? 60 : timeInMinutes! % 60}min`
            : '0';
    };

    useEffect(() => {
        if (props.appointment.variableData && props.appointment.variableData.length > 0) {
            const appointments = props.appointment.variableData?.sort((wrapper, otherWrapper) =>
                wrapper.date.isBefore(otherWrapper.date) ? -1 : 1
            );
            const appointmentsSeparatedDate: Array<Array<DataVaraible>> | undefined = [];
            let arrayStart = 0;
            for (let i = 1; i <= appointments.length; i++) {
                if (i < appointments.length) {
                    if (appointments[i - 1].date.format('L') !== appointments[i].date.format('L')) {
                        appointmentsSeparatedDate.push(appointments.slice(arrayStart, i));
                        arrayStart = i;
                    }
                }
                if (i === appointments.length) {
                    appointmentsSeparatedDate.push(appointments.slice(arrayStart, appointments.length));
                }
            }
            setAppointmentsSeparated(appointmentsSeparatedDate);
        }
        if (props.appointment) {
            setPet(petsState.list.find((pet: Pet) => pet.id === props.appointment.petId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.appointment, petsState.list]);

    const getItem = (data: DataVaraible, type: itemTypes, edit?: boolean) => {
        const items = {
            [itemTypes.service]: edit ? 'Service' : getServicesTime(data.service),
            'add-on': 'Add-On',
            [itemTypes.product]: 'Product',
            [itemTypes.custom]: 'Custom'
        };
        return items[type] || undefined;
    };

    const getType = (data: DataVaraible) => {
        let type = itemTypes.service;
        if (data.service) {
            type = itemTypes.service;
        } else if (data.product) {
            type = itemTypes.product;
        } else if (data.addOn) {
            type = itemTypes.addOn;
        } else if (data.customDescription) {
            type = itemTypes.custom;
        }
        return type;
    };

    const getName = (data: DataVaraible, type: itemTypes) => {
        const items = {
            [itemTypes.service]: data.service?.service.name,
            'add-on': data.addOn?.name,
            [itemTypes.product]: data.product?.name,
            [itemTypes.custom]: data.customDescription
        };
        return items[type] || undefined;
    };

    const getItemToDelete = (data: DataVaraible, type: itemTypes) => {
        const items = {
            [itemTypes.service]: data.service?.service?.id,
            [itemTypes.custom]: data.customDescription,
            'add-on': data.addOn?.id,
            [itemTypes.product]: data.product?.id
        };
        return items[type] || undefined;
    };

    const getPriceWithDiscount = (data: DataVaraible, total?: number) => {
        if (data.itemData?.discount?.type === 'percentage' && total) {
            const amountToDiscount = (data.itemData?.discount?.value * total) / 100;
            return total - amountToDiscount;
        } else if (data.itemData?.discount?.type === 'amount' && total) {
            return total - data.itemData?.discount?.value;
        }
    };

    const countExpiredVaccines = (): number => {
        if (!pet?.medicalHistory?.vaccines) return 0;
        const today = moment().tz(timezone).clone().startOf('day');

        const expiredVaccines: Array<PetVaccine> = [];

        pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded) {
                if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                    const index = expiredVaccines.indexOf(vaccineFounded);
                    expiredVaccines[index] = { ...vaccine };
                }
            } else {
                if (vaccine.status === 'expired') {
                    expiredVaccines.push({ ...vaccine });
                }
            }
        });

        pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                const index = expiredVaccines.indexOf(vaccineFounded);
                expiredVaccines[index] = { ...vaccine };
            }
        });

        pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                const index = expiredVaccines.indexOf(vaccineFounded);
                expiredVaccines[index] = { ...vaccine };
            }
        });

        return expiredVaccines.filter(vaccine => {
            if (!vaccine.expiration) return false;
            return vaccine.expiration.isSameOrBefore(today, 'day');
        }).length;
    };

    return (
        <Box className={classes.container}>
            {countExpiredVaccines() > 0 && (
                <Box
                    className={classes.alertAndAddContainer}
                    onClick={() => props.onShowPetCard && props.onShowPetCard(props.appointment.petId)}
                >
                    <AlertNewBooking
                        className={classes.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EF4F57"
                        bgColor="#FBB9BC33"
                        bdColor="#EF4F57"
                    >
                        <Typography className={classes.alertText}>
                            {pet?.name} has <span className={classes.bold}>{countExpiredVaccines()}</span> expired
                            vaccine. Please review & <a className={classes.update}>update</a>
                        </Typography>
                    </AlertNewBooking>
                </Box>
            )}
            <Box className={clsx(classes.petBox, classes.spacing)}>
                <Box className={classes.petCardContainer}>
                    <PetCard pet={petsState.list.find(pet => pet.id === props.appointment.petId)} />
                </Box>
                <Box className={classes.deleteBox}>
                    <Box
                        className={classes.deleteContainer}
                        onClick={() => props.onDelete(props.appointment.petId)}
                    >
                        <FontAwesomeIcon icon={faTrashCan} />
                    </Box>
                </Box>
            </Box>
            {appointmentsSeparated?.map((appts: Array<DataVaraible>) =>
                appts?.map((data: DataVaraible, index) => (
                    <>
                        {index === 0 && (
                            <Box className={classes.dateContainer}>
                                <Typography className={classes.date}>
                                    {data.date.format('dddd, DD MMM YYYY')}
                                </Typography>
                            </Box>
                        )}
                        <Box
                            className={classes.serviceStaffContainer}
                            key={data.service?.service.id}
                        >
                            <Box className={classes.itemContainer}>
                                <Box
                                    className={classes.emptyBox}
                                    style={{
                                        borderLeftColor:
                                            services.find((serv: Service) => serv.id === data.service?.service?.id)
                                                ?.color ?? '#ffffff'
                                    }}
                                />
                                <Box
                                    className={clsx(classes.divide, {
                                        [classes.withoutDivider]: index === appts.length - 1
                                    })}
                                    style={{ width: '91%' }}
                                >
                                    <Typography className={classes.serviceName}>
                                        <span
                                            className={clsx(classes.serviceNameText, {
                                                [classes.rightSpace]: getType(data) === itemTypes.service
                                            })}
                                        >
                                            {getName(data, getType(data))}
                                        </span>
                                        {`${data.itemData?.quantity ? '(' : ' · '}${
                                            data.itemData?.quantity ? data.itemData?.quantity : ''
                                        }${data.itemData?.quantity ? ') ·' : ''}`}
                                        {data.service ? (
                                            <>
                                                <Box className={classes.serviceData}>
                                                    {!!data?.itemData?.discount?.value &&
                                                        data?.itemData?.discount?.value !== 0 && (
                                                            <Typography
                                                                className={clsx(classes.beforePrice, classes.price)}
                                                            >
                                                                ${Number(data.service?.price)?.toFixed(2)}
                                                            </Typography>
                                                        )}
                                                    {!!data?.itemData?.discount?.value &&
                                                        data?.itemData?.discount?.value !== 0 && (
                                                            <Typography className={classes.price}>
                                                                $
                                                                {getPriceWithDiscount(
                                                                    data,
                                                                    data.service?.price
                                                                )?.toFixed(2)}
                                                            </Typography>
                                                        )}
                                                </Box>
                                                {data?.itemData?.discount?.value === undefined ||
                                                    (data?.itemData?.discount?.value === 0 && (
                                                        <Typography className={classes.price}>
                                                            {'$' + data?.service?.price?.toFixed(2)}
                                                        </Typography>
                                                    ))}
                                            </>
                                        ) : (
                                            <Box>
                                                {data?.itemData?.discount?.value === undefined ||
                                                data?.itemData?.discount?.value === 0 ? (
                                                    <Typography className={classes.price}>
                                                        ${data?.itemData?.total?.toFixed(2)}
                                                    </Typography>
                                                ) : (
                                                    <Box className={classes.serviceData}>
                                                        <Typography
                                                            className={clsx(classes.beforePrice, classes.price)}
                                                        >
                                                            ${data.itemData?.total?.toFixed(2)}
                                                        </Typography>
                                                        <Typography className={classes.price}>
                                                            $
                                                            {getPriceWithDiscount(data, data.itemData?.total)?.toFixed(
                                                                2
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Typography>
                                    <Box className={classes.serviceDataContainer}>
                                        <Box className={classes.dataContainer}>
                                            {data.service && (
                                                <Typography className={classes.priceAndDuration}>
                                                    {data.date.format('LT')} ·
                                                </Typography>
                                            )}
                                            <Box className={classes.dataContainer}>
                                                <Typography
                                                    className={clsx(classes.priceAndDuration, classes.spacingDuration)}
                                                >
                                                    {getItem(data, getType(data))}
                                                </Typography>
                                            </Box>
                                            <Typography className={classes.priceAndDuration}>
                                                ·{' '}
                                                {data.staffId ? formatStaffToStaffMember(data.staffId!) : 'Unassigned'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box
                                    className={clsx(classes.divide, classes.serviceOptionsContainer, {
                                        [classes.withoutDivider]: index === appts.length - 1
                                    })}
                                >
                                    <ServiceOptions
                                        className={classes.serviceOptions}
                                        item={getItem(data, getType(data), true)}
                                        onClickMobile={() =>
                                            props.onShowActions(
                                                props.appointment.petId,
                                                data.date,
                                                data.itemData,
                                                data.service,
                                                data.staffId,
                                                data.product,
                                                data.addOn,
                                                data.customDescription,
                                                getType(data)
                                            )
                                        }
                                        onEdit={() =>
                                            props.onEdit(
                                                props.appointment.petId,
                                                data.date,
                                                data.staffId,
                                                data.itemData,
                                                data.service,
                                                data.product,
                                                data.addOn,
                                                data.customDescription,
                                                getType(data)
                                            )
                                        }
                                        onDeleteAppointment={() =>
                                            props.onDeleteAppointment(
                                                props.appointment.petId,
                                                getItemToDelete(data, getType(data)),
                                                getType(data),
                                                data.date
                                            )
                                        }
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </>
                ))
            )}
            <AppointmentOptions
                className={classes.alertAndAddContainer}
                onAddService={props.onAddService}
                onAddAddOn={() => props.onAddAddOn(props.appointment.petId)}
                onAddProduct={() => props.onAddProduct(props.appointment.petId)}
                onAddCustomItem={() => props.onAddCustomItem(props.appointment.petId)}
            />
        </Box>
    );
};

export default AppointmentCard;
